import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

import {
    Download,
    Trash2,
    Mail,
    Phone,
    Calendar,
    Building,
    MessageSquare,
    LayoutGrid,
    List,
    X,
    Eye,
    CheckCircle,
    AlertCircle,
    Globe,
    RotateCcw
} from 'lucide-react';
import { Button } from '../../ui/button';
import { toast } from 'sonner';
import { Badge } from '../../ui/badge';
import { AdminLayout } from './components/AdminLayout';
import { AdminTopBar } from './components/AdminTopBar';
import { SearchAndFilters } from './components/SearchAndFilters';
import { BackLink } from './components/BackLink';
import { EmptyState } from './components/EmptyState';
import { Pagination } from './components/Pagination';
import { StatCard } from './components/StatCard';
import { enquiriesAPI } from '../../../services/api';
import { DeleteConfirmationModal } from './components/DeleteConfirmationModal';
import { useSocketEvent } from '../../contexts/SocketContext';

interface Enquiry {
    id: string;
    name: string;
    email: string;
    phone: string;
    company: string;
    country: string;
    subject: string;
    message: string;
    status: 'new' | 'need_to_contact' | 'replied';
    visibility?: 'visible' | 'hidden';
    date: string;
    priority: 'low' | 'medium' | 'high';
}

export function AdminEnquiries() {
    const [enquiries, setEnquiries] = useState<Enquiry[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [statusFilter, setStatusFilter] = useState<string>('all');
    const [priorityFilter, setPriorityFilter] = useState<string>('all');
    const [dateFilter, setDateFilter] = useState<string>('all');
    const [customDateRange, setCustomDateRange] = useState<{ start: string; end: string }>({ start: '', end: '' });
    const [selectedEnquiry, setSelectedEnquiry] = useState<Enquiry | null>(null);
    const [currentPage, setCurrentPage] = useState(1);
    const [viewMode, setViewMode] = useState<'list' | 'grid'>('list');
    const [showViewAll, setShowViewAll] = useState(false);
    const itemsPerPage = viewMode === 'grid' ? 6 : 5;
    const [deleteModal, setDeleteModal] = useState({
        isOpen: false,
        itemId: '',
        itemName: '',
        isDeleting: false
    });

    const handleStatusChange = async (id: string, newStatus: Enquiry['status']) => {
        try {
            // Update via API
            await enquiriesAPI.update(id, { status: newStatus });
            
            // Update local state
            setEnquiries(enquiries.map(e =>
                e.id === id ? { ...e, status: newStatus } : e
            ));
            
            // Also update selectedEnquiry if it's the same one
            if (selectedEnquiry?.id === id) {
                setSelectedEnquiry({ ...selectedEnquiry, status: newStatus });
            }
            
            toast.success('Status updated successfully');
        } catch (error) {
            console.error('Error updating status:', error);
            toast.error('Failed to update status');
        }
    };

    const handlePriorityChange = async (id: string, newPriority: Enquiry['priority']) => {
        try {
            // Update via API
            await enquiriesAPI.update(id, { priority: newPriority });
            
            // Update local state
            setEnquiries(enquiries.map(e =>
                e.id === id ? { ...e, priority: newPriority } : e
            ));
            
            // Also update selectedEnquiry if it's the same one
            if (selectedEnquiry?.id === id) {
                setSelectedEnquiry({ ...selectedEnquiry, priority: newPriority });
            }
            
            toast.success('Priority updated successfully');
        } catch (error) {
            console.error('Error updating priority:', error);
            toast.error('Failed to update priority');
        }
    };

    const handleSelectEnquiry = (enquiry: Enquiry) => {
        setSelectedEnquiry(enquiry);
        // Auto-mark as need_to_contact if it's new
        if (enquiry.status === 'new') {
            handleStatusChange(enquiry.id, 'need_to_contact');
        }
    };

    const handleDelete = (id: string) => {
        setEnquiries(enquiries.filter(e => e.id !== id));
        setSelectedEnquiry(null);
        toast.success('Enquiry deleted');
    };

    // Helper function to check if a date falls within the selected date range
    const isWithinDateRange = (dateString: string) => {
        if (dateFilter === 'all') return true;
        
        const date = new Date(dateString);
        const now = new Date();
        const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
        
        switch (dateFilter) {
            case 'today':
                return date >= today;
            case 'last7days':
                const sevenDaysAgo = new Date(today);
                sevenDaysAgo.setDate(today.getDate() - 7);
                return date >= sevenDaysAgo;
            case 'last15days':
                const fifteenDaysAgo = new Date(today);
                fifteenDaysAgo.setDate(today.getDate() - 15);
                return date >= fifteenDaysAgo;
            case 'last30days':
                const thirtyDaysAgo = new Date(today);
                thirtyDaysAgo.setDate(today.getDate() - 30);
                return date >= thirtyDaysAgo;
            case 'custom':
                if (!customDateRange.start || !customDateRange.end) return true;
                const startDate = new Date(customDateRange.start);
                const endDate = new Date(customDateRange.end);
                return date >= startDate && date <= endDate;
            default:
                return true;
        }
    };

    const filteredEnquiries = enquiries.filter(enquiry => {
        const matchesSearch =
            enquiry.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
            enquiry.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
            enquiry.company.toLowerCase().includes(searchTerm.toLowerCase()) ||
            enquiry.subject.toLowerCase().includes(searchTerm.toLowerCase());

        const matchesStatus = statusFilter === 'all' || enquiry.status === statusFilter;
        const matchesPriority = priorityFilter === 'all' || enquiry.priority === priorityFilter;
        const matchesDate = isWithinDateRange(enquiry.date);

        return matchesSearch && matchesStatus && matchesPriority && matchesDate;
    });

    const totalPages = Math.ceil(filteredEnquiries.length / itemsPerPage);
    const paginatedEnquiries = filteredEnquiries.slice(
        (currentPage - 1) * itemsPerPage,
        currentPage * itemsPerPage
    );

    const statusColors = {
        new: 'bg-orange-500/20 text-orange-400 border-orange-500/30',
        need_to_contact: 'bg-blue-500/20 text-blue-400 border-blue-500/30',
        replied: 'bg-green-500/20 text-green-400 border-green-500/30'
    };

    const priorityColors = {
        low: 'bg-gray-500/20 text-gray-400 border-gray-500/30',
        medium: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30',
        high: 'bg-red-500/20 text-red-400 border-red-500/30'
    };

    const visibilityColors = {
        visible: '',
        hidden: 'bg-red-500/20 text-red-400 border-red-500/30'
    };

    // Get current user from localStorage to check if super_admin
    const currentUser = JSON.parse(localStorage.getItem('adminUser') || '{}');
    const isSuperAdmin = currentUser?.role === 'super_admin';

    const handleRestore = async (id: string) => {
        try {
            await enquiriesAPI.update(id, { visibility: 'visible' });
            setEnquiries(enquiries.map(e =>
                e.id === id ? { ...e, visibility: 'visible' } : e
            ));
            if (selectedEnquiry?.id === id) {
                setSelectedEnquiry({ ...selectedEnquiry, visibility: 'visible' });
            }
            toast.success('Enquiry restored successfully');
        } catch (error) {
            console.error('Error restoring enquiry:', error);
            toast.error('Failed to restore enquiry');
        }
    };
    useEffect(() => {
        const fetchEnquiries = async () => {
            try {
                setIsLoading(true);
                // Adjust params if your backend pagination expects different names
                const res = await enquiriesAPI.getAll({ page: 1, limit: 20 });

                // Depending on your backend shape, data may be under:
                // res.data, res.enquiries, res.results, etc.
                const list = res.data || res.enquiries || res.items || [];

                const mapped: Enquiry[] = list.filter((e: any) => e && (e._id || e.id)).map((e: any) => ({
                    id: e._id || e.id,
                    name: e.name,
                    email: e.email,
                    phone: e.phone || '',
                    company: e.company || '',
                    country: e.country || '',
                    subject: e.subject,
                    message: e.message,
                    status: e.status || 'new',
                    visibility: e.visibility || 'visible',
                    priority: e.priority || 'medium',
                    date: e.date || e.createdAt || new Date().toISOString(),
                }));

                setEnquiries(mapped);
            } catch (err) {
                console.error('Error fetching enquiries:', err);
                toast.error('Failed to load enquiries from database');
                setEnquiries([]);
            } finally {
                setIsLoading(false);
            }
        };

        fetchEnquiries();
    }, []);

    // Real-time socket event handlers
    useSocketEvent('enquiry:created', useCallback((data: any) => {
        if (!data?.enquiry) return; // Guard against undefined data
        const enquiry = data.enquiry;
        const newEnquiry: Enquiry = {
            id: enquiry._id || enquiry.id,
            name: enquiry.name,
            email: enquiry.email,
            phone: enquiry.phone || '',
            company: enquiry.company || '',
            country: enquiry.country || '',
            subject: enquiry.subject,
            message: enquiry.message,
            status: enquiry.status || 'new',
            visibility: enquiry.visibility || 'visible',
            priority: enquiry.priority || 'medium',
            date: enquiry.date || enquiry.createdAt || new Date().toISOString(),
        };
        setEnquiries(prev => [newEnquiry, ...prev]);
        toast.info('New enquiry received!');
    }, []));

    useSocketEvent('enquiry:updated', useCallback((data: any) => {
        if (!data?.enquiry) return; // Guard against undefined data
        const enquiry = data.enquiry;
        setEnquiries(prev => prev.map(e => {
            if (e.id === (enquiry._id || enquiry.id)) {
                return {
                    ...e,
                    name: enquiry.name ?? e.name,
                    email: enquiry.email ?? e.email,
                    phone: enquiry.phone ?? e.phone,
                    company: enquiry.company ?? e.company,
                    country: enquiry.country ?? e.country,
                    subject: enquiry.subject ?? e.subject,
                    message: enquiry.message ?? e.message,
                    status: enquiry.status ?? e.status,
                    visibility: enquiry.visibility ?? e.visibility,
                    priority: enquiry.priority ?? e.priority,
                };
            }
            return e;
        }));
        toast.info('Enquiry updated');
    }, []));

    useSocketEvent('enquiry:deleted', useCallback((data: any) => {
        if (!data) return; // Guard against undefined data
        const deletedId = data.enquiry?._id || data.enquiry?.id || data.id;
        if (!deletedId) return;
        setEnquiries(prev => prev.filter(e => e.id !== deletedId));
        toast.info('Enquiry removed');
    }, []));

    // Calculate stats
    const totalEnquiries = enquiries.length;
    const newEnquiries = enquiries.filter(e => e.status === 'new').length;
    const repliedEnquiries = enquiries.filter(e => e.status === 'replied').length;
    const needToContactEnquiries = enquiries.filter(e => e.status === 'need_to_contact').length;

    const stats = [
        {
            icon: MessageSquare,
            label: 'Total Enquiries',
            value: isLoading ? null : totalEnquiries.toString(),
            change: 'All time',
            changeType: 'positive' as const,
            iconColor: 'text-cyan-400',
            iconBgColor: 'bg-cyan-400/10'
        },
        {
            icon: AlertCircle,
            label: 'New Enquiries',
            value: isLoading ? null : newEnquiries.toString(),
            change: 'Unread',
            changeType: 'positive' as const,
            iconColor: 'text-orange-400',
            iconBgColor: 'bg-orange-400/10'
        },
        {
            icon: Eye,
            label: 'Need to Contact',
            value: isLoading ? null : needToContactEnquiries.toString(),
            change: 'Awaiting response',
            changeType: 'neutral' as const,
            iconColor: 'text-blue-400',
            iconBgColor: 'bg-blue-400/10'
        },
        {
            icon: CheckCircle,
            label: 'Replied',
            value: isLoading ? null : repliedEnquiries.toString(),
            change: 'Completed',
            changeType: 'positive' as const,
            iconColor: 'text-green-400',
            iconBgColor: 'bg-green-400/10'
        }
    ];

    return (
        <AdminLayout>
            <AdminTopBar
                title="Contact Enquiries"
                description="Manage and respond to customer enquiries"
                actionButton={{
                    label: 'Export',
                    icon: <Download className="w-4 h-4 mr-2" />,
                    onClick: () => toast.info('Export feature coming soon')
                }}
            />

            <div className="p-8 space-y-6">
                {/* Stats Cards */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                    {stats.map((stat, index) => (
                        <StatCard
                            key={index}
                            icon={stat.icon}
                            title={stat.label}
                            value={stat.value}
                            change={stat.change}
                            changeType={stat.changeType}
                            iconColor={stat.iconColor}
                            iconBgColor={stat.iconBgColor}
                        />
                    ))}
                </div>

                <SearchAndFilters
                    searchValue={searchTerm}
                    onSearchChange={setSearchTerm}
                    searchPlaceholder="Search by name, email, company, or subject..."
                    filters={[
                        {
                            value: statusFilter,
                            onChange: setStatusFilter,
                            options: [
                                { value: 'all', label: 'All Status' },
                                { value: 'new', label: 'New' },
                                { value: 'need_to_contact', label: 'Need to Contact' },
                                { value: 'replied', label: 'Replied' }
                            ],
                            label: 'Status Filter'
                        },
                        {
                            value: priorityFilter,
                            onChange: setPriorityFilter,
                            options: [
                                { value: 'all', label: 'All Priority' },
                                { value: 'high', label: 'High' },
                                { value: 'medium', label: 'Medium' },
                                { value: 'low', label: 'Low' }
                            ],
                            label: 'Priority Filter'
                        },
                        {
                            value: dateFilter,
                            onChange: setDateFilter,
                            options: [
                                { value: 'all', label: 'All Time' },
                                { value: 'today', label: 'Today' },
                                { value: 'last7days', label: 'Last 7 Days' },
                                { value: 'last15days', label: 'Last 15 Days' },
                                { value: 'last30days', label: 'Last 30 Days' },
                                { value: 'custom', label: 'Custom Date' }
                            ],
                            label: 'Date Filter'
                        }
                    ]}
                    dateFilter={dateFilter}
                    customDateRange={customDateRange}
                    onCustomDateRangeChange={setCustomDateRange}
                />



                {/* Enquiries List and Detail View */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 items-start">
                    {/* List */}
                    <div className="glass-strong rounded-xl border border-primary/30 p-6">
                        <div className="flex items-center justify-between mb-4">
                            <h3 className="text-foreground">
                                Enquiries ({isLoading ? '...' : filteredEnquiries.length})
                            </h3>
                            {/* View Toggle */}
                            <div className="flex items-center justify-between">
                                <div className="flex gap-2 glass-strong rounded-lg border border-primary/30 p-1">
                                    <Button
                                        variant="ghost"
                                        size="sm"
                                        onClick={() => setViewMode('list')}
                                        className={viewMode === 'list' ? 'bg-primary/20 text-primary' : 'text-muted-foreground'}
                                    >
                                        <List className="w-4 h-4 mr-2" />

                                    </Button>
                                    <Button
                                        variant="ghost"
                                        size="sm"
                                        onClick={() => setViewMode('grid')}
                                        className={viewMode === 'grid' ? 'bg-primary/20 text-primary' : 'text-muted-foreground'}
                                    >
                                        <LayoutGrid className="w-4 h-4 mr-2" />

                                    </Button>
                                </div>
                            </div>
                            <Button
                                variant="outline"
                                size="sm"
                                onClick={() => setShowViewAll(true)}
                                className="glass border-primary/30 text-primary hover:bg-primary/10"
                            >
                                <Eye className="w-4 h-4 mr-2" />
                                View All
                            </Button>
                        </div>
                        <div className={viewMode === 'grid' ? 'grid grid-cols-2 gap-4' : 'space-y-2'}>
                            {isLoading ? (
                                // Loading skeleton
                                Array.from({ length: viewMode === 'grid' ? 6 : 5 }).map((_, index) => (
                                    <div key={index} className={`p-4 rounded-lg glass ${viewMode === 'grid' ? 'h-full' : ''}`}>
                                        <div className="flex items-start justify-between mb-2">
                                            <div className="flex-1 min-w-0">
                                                <div className="h-4 w-24 bg-muted/20 animate-pulse rounded mb-1"></div>
                                                <div className="h-3 w-20 bg-muted/20 animate-pulse rounded"></div>
                                            </div>
                                            <div className="flex gap-2 ml-2">
                                                <div className="h-5 w-12 bg-muted/20 animate-pulse rounded"></div>
                                            </div>
                                        </div>
                                        <div className="h-4 w-32 bg-muted/20 animate-pulse rounded mb-2"></div>
                                        <div className="flex items-center justify-between">
                                            <div className="h-5 w-16 bg-muted/20 animate-pulse rounded"></div>
                                            <div className="h-3 w-16 bg-muted/20 animate-pulse rounded"></div>
                                        </div>
                                    </div>
                                ))
                            ) : (
                                <>
                                    {paginatedEnquiries.map((enquiry) => (
                                <motion.div
                                    key={enquiry.id}
                                    whileHover={{ x: viewMode === 'list' ? 4 : 0, scale: viewMode === 'grid' ? 1.02 : 1 }}
                                    onClick={() => handleSelectEnquiry(enquiry)}
                                    className={`p-4 rounded-lg glass cursor-pointer transition-all ${selectedEnquiry?.id === enquiry.id
                                            ? 'bg-primary/10 border border-primary/30'
                                            : 'hover:bg-primary/5'
                                        } ${viewMode === 'grid' ? 'h-full' : ''}`}
                                >
                                    <div className="flex items-start justify-between mb-2">
                                        <div className="flex-1 min-w-0">
                                            <h4 className="text-foreground truncate">{enquiry.name}</h4>
                                            <p className="text-sm text-muted-foreground truncate">
                                                {enquiry.company}
                                            </p>
                                        </div>
                                        <div className="flex gap-2 ml-2">
                                            <Badge className={`text-xs ${priorityColors[enquiry.priority]}`}>
                                                {enquiry.priority}
                                            </Badge>
                                            {enquiry.visibility === 'hidden' && (
                                                <Badge className={`text-xs ${visibilityColors.hidden}`}>
                                                    Deleted
                                                </Badge>
                                            )}
                                        </div>
                                    </div>
                                    <p className="text-sm text-foreground mb-2 truncate">
                                        {enquiry.subject}
                                    </p>
                                    <div className="flex items-center justify-between">
                                        <Badge className={`text-xs ${statusColors[enquiry.status]}`}>
                                            {enquiry.status}
                                        </Badge>
                                        <span className="text-xs text-muted-foreground">
                                            {new Date(enquiry.date).toLocaleDateString()}
                                        </span>
                                    </div>
                                </motion.div>
                            ))}
                                </>
                            )}
                            {!isLoading && filteredEnquiries.length === 0 && (
                                <EmptyState
                                    icon={MessageSquare}
                                    title="No enquiries found"
                                    description="Try adjusting your search or filters"
                                />
                            )}
                        </div>
                        {!isLoading && filteredEnquiries.length > 0 && (
                            <div className="mt-4">
                                <Pagination
                                    currentPage={currentPage}
                                    totalPages={totalPages}
                                    onPageChange={setCurrentPage}
                                    itemsPerPage={itemsPerPage}
                                    totalItems={filteredEnquiries.length}
                                />
                            </div>
                        )}
                    </div>

                    {/* Detail View */}
                    <div className="glass-strong rounded-xl border border-primary/30 p-6 sticky top-0 lg:top-2 self-start max-h-screen overflow-y-auto z-20">
                        {selectedEnquiry ? (
                            <div className="space-y-6">
                                <div className="flex items-start justify-between">
                                    <div>
                                        <h3 className="text-foreground mb-1">{selectedEnquiry.name}</h3>
                                        <div className="flex gap-2 mb-2 flex-wrap">
                                            <Badge className={`text-xs ${statusColors[selectedEnquiry.status]}`}>
                                                {selectedEnquiry.status}
                                            </Badge>
                                            <Badge className={`text-xs ${priorityColors[selectedEnquiry.priority]}`}>
                                                {selectedEnquiry.priority}
                                            </Badge>
                                            {selectedEnquiry.visibility === 'hidden' && (
                                                <Badge className={`text-xs ${visibilityColors.hidden}`}>
                                                    Deleted
                                                </Badge>
                                            )}
                                        </div>
                                    </div>
                                    <div className="flex gap-1">
                                        {isSuperAdmin && selectedEnquiry.visibility === 'hidden' && (
                                            <Button
                                                variant="ghost"
                                                size="icon"
                                                onClick={() => handleRestore(selectedEnquiry.id)}
                                                className="text-green-400 hover:text-green-300 hover:bg-green-500/10"
                                                title="Restore Enquiry"
                                            >
                                                <RotateCcw className="w-4 h-4" />
                                            </Button>
                                        )}
                                        <Button
                                            variant="ghost"
                                            size="icon"
                                            onClick={() => setDeleteModal({
                                                isOpen: true,
                                                itemId: selectedEnquiry.id,
                                                itemName: selectedEnquiry.name,
                                                isDeleting: false
                                            })}
                                            className="text-red-400 hover:text-red-300 hover:bg-red-500/10"
                                            title={isSuperAdmin ? "Permanent Delete" : "Delete"}
                                        >
                                            <Trash2 className="w-4 h-4" />
                                        </Button>
                                    </div>
                                </div>

                                <div className="space-y-4">
                                    <div className="flex items-center gap-2 text-sm">
                                        <Building className="w-4 h-4 text-muted-foreground" />
                                        <span className="text-foreground">{selectedEnquiry.company}</span>
                                    </div>
                                    <div className="flex items-center gap-2 text-sm">
                                        <Globe className="w-4 h-4 text-muted-foreground" />
                                        <span className="text-foreground">{selectedEnquiry.country || 'Not specified'}</span>
                                    </div>
                                    <div className="flex items-center gap-2 text-sm">
                                        <Mail className="w-4 h-4 text-muted-foreground" />
                                        <a
                                            href={`mailto:${selectedEnquiry.email}`}
                                            className="text-primary hover:text-primary/80"
                                        >
                                            {selectedEnquiry.email}
                                        </a>
                                    </div>
                                    <div className="flex items-center gap-2 text-sm">
                                        <Phone className="w-4 h-4 text-muted-foreground" />
                                        <span className="text-foreground">{selectedEnquiry.phone}</span>
                                    </div>
                                    <div className="flex items-center gap-2 text-sm">
                                        <Calendar className="w-4 h-4 text-muted-foreground" />
                                        <span className="text-foreground">
                                            {new Date(selectedEnquiry.date).toLocaleString()}
                                        </span>
                                    </div>
                                </div>

                                <div>
                                    <h4 className="text-foreground mb-2">Subject</h4>
                                    <p className="text-foreground">{selectedEnquiry.subject}</p>
                                </div>

                                <div>
                                    <h4 className="text-foreground mb-2">Message</h4>
                                    <p className="text-muted-foreground">{selectedEnquiry.message}</p>
                                </div>

                                <div className="flex gap-2 pt-4 border-t border-border/30">
                                    <Button
                                        onClick={() => handleStatusChange(selectedEnquiry.id, 'replied')}
                                        className="flex-1 bg-primary hover:bg-primary/90"
                                    >
                                        <Mail className="w-4 h-4 mr-2" />
                                        Reply
                                    </Button>
                                    <select
                                        value={selectedEnquiry.status}
                                        onChange={(e) => handleStatusChange(selectedEnquiry.id, e.target.value as Enquiry['status'])}
                                        className="px-4 py-2 rounded-lg glass border border-border/30 text-foreground bg-background"
                                    >
                                        <option value="new">New</option>
                                        <option value="need_to_contact">Need to Contact</option>
                                        <option value="replied">Replied</option>
                                    </select>
                                    <select
                                        value={selectedEnquiry.priority}
                                        onChange={(e) => handlePriorityChange(selectedEnquiry.id, e.target.value as Enquiry['priority'])}
                                        className="px-4 py-2 rounded-lg glass border border-border/30 text-foreground bg-background"
                                    >
                                        <option value="low">Low</option>
                                        <option value="medium">Medium</option>
                                        <option value="high">High</option>
                                    </select>
                                </div>
                            </div>
                        ) : (
                            <div className="flex items-center justify-center h-full text-muted-foreground">
                                Select an enquiry to view details
                            </div>
                        )}
                    </div>
                </div>

                <BackLink to="/admin" label="Back to Dashboard" />
            </div>

            {/* View All Modal */}
            <AnimatePresence>
                {showViewAll && (
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4"
                        onClick={() => setShowViewAll(false)}
                    >
                        <motion.div
                            initial={{ scale: 0.9, y: 20 }}
                            animate={{ scale: 1, y: 0 }}
                            exit={{ scale: 0.9, y: 20 }}
                            onClick={(e) => e.stopPropagation()}
                            className="glass-strong rounded-2xl border border-primary/30 p-6 w-full max-w-6xl max-h-[90vh] overflow-hidden flex flex-col"
                        >
                            <div className="flex items-center justify-between mb-6">
                                <h2 className="text-foreground">
                                    All Enquiries ({filteredEnquiries.length})
                                </h2>
                                <Button
                                    variant="ghost"
                                    size="icon"
                                    onClick={() => setShowViewAll(false)}
                                >
                                    <X className="w-5 h-5" />
                                </Button>
                            </div>

                            <div className="overflow-y-auto flex-1">
                                <div className="space-y-2">
                                    {filteredEnquiries.slice(0, 50).map((enquiry) => (
                                        <motion.div
                                            key={enquiry.id}
                                            whileHover={{ x: 4 }}
                                            onClick={() => {
                                                handleSelectEnquiry(enquiry);
                                                setShowViewAll(false);
                                            }}
                                            className="p-4 rounded-lg glass cursor-pointer hover:bg-primary/5 transition-all"
                                        >
                                            <div className="flex items-start justify-between mb-2">
                                                <div className="flex-1 min-w-0">
                                                    <h4 className="text-foreground truncate">{enquiry.name}</h4>
                                                    <p className="text-sm text-muted-foreground truncate">
                                                        {enquiry.company}
                                                    </p>
                                                </div>
                                                <div className="flex gap-2 ml-2 flex-wrap">
                                                    <Badge className={`text-xs ${priorityColors[enquiry.priority]}`}>
                                                        {enquiry.priority}
                                                    </Badge>
                                                    {enquiry.visibility === 'hidden' && (
                                                        <Badge className={`text-xs ${visibilityColors.hidden}`}>
                                                            Deleted
                                                        </Badge>
                                                    )}
                                                </div>
                                            </div>
                                            <p className="text-sm text-foreground mb-2 truncate">
                                                {enquiry.subject}
                                            </p>
                                            <div className="flex items-center justify-between">
                                                <Badge className={`text-xs ${statusColors[enquiry.status]}`}>
                                                    {enquiry.status}
                                                </Badge>
                                                <span className="text-xs text-muted-foreground">
                                                    {new Date(enquiry.date).toLocaleDateString()}
                                                </span>
                                            </div>
                                        </motion.div>
                                    ))}
                                </div>
                            </div>
                        </motion.div>
                    </motion.div>
                )}
            </AnimatePresence>

            {/* Delete Confirmation Modal */}
            <DeleteConfirmationModal
                isOpen={deleteModal.isOpen}
                onClose={() => !deleteModal.isDeleting && setDeleteModal({ isOpen: false, itemId: '', itemName: '', isDeleting: false })}
                onConfirm={async () => {
                    setDeleteModal(prev => ({ ...prev, isDeleting: true }));
                    try {
                        await enquiriesAPI.delete(deleteModal.itemId);
                        if (isSuperAdmin) {
                            // Super admin: item is permanently deleted, remove from list
                            const updatedEnquiries = enquiries.filter(e => e.id !== deleteModal.itemId);
                            setEnquiries(updatedEnquiries);
                        } else {
                            // Regular admin: item is soft-deleted, update visibility
                            const updatedEnquiries = enquiries.map(e =>
                                e.id === deleteModal.itemId ? { ...e, visibility: 'hidden' as const } : e
                            );
                            setEnquiries(updatedEnquiries);
                        }
                        if (selectedEnquiry?.id === deleteModal.itemId) {
                            setSelectedEnquiry(null);
                        }
                        toast.success(isSuperAdmin ? 'Enquiry permanently deleted' : 'Enquiry deleted successfully');
                        setDeleteModal({
                            isOpen: false,
                            itemId: '',
                            itemName: '',
                            isDeleting: false
                        });
                    } catch (error) {
                        toast.error('Failed to delete enquiry');
                        setDeleteModal(prev => ({ ...prev, isDeleting: false }));
                    }
                }}
                title={isSuperAdmin ? "Permanently Delete Enquiry" : "Delete Enquiry"}
                message={isSuperAdmin ? "Are you sure you want to permanently delete this enquiry?" : "Are you sure you want to delete this enquiry?"}
                itemName={deleteModal.itemName}
                isDeleting={deleteModal.isDeleting}
                isPermanent={isSuperAdmin}
            />
        </AdminLayout>
    );
}
